import { Command, Option } from "clipanion";
import { execFileSync, spawnSync } from "node:child_process";
import { readFileSync } from "node:fs";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import * as readline from "node:readline/promises";
import { DefaultAzureCredential } from "@azure/identity";
import { appHealthFailureCopy, checkAppHealth, readAppSecrets, signAppJwt } from "@m8t-stack/github-app-auth";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { getAzAccount } from "../../lib/auth.js";
import { readAgentYaml } from "../../lib/agent-yaml.js";
import {
  isGhAuthed, ghAuthLoginDevice, createRepo, getRepoId,
  installUrl, tryOpenUrl, pollForInstallation, probeAppInstallation, ghRepoProbe, getGhToken,
} from "../../lib/gh-helpers.js";
import { linkBrain } from "../../lib/brain-link.js";
import { discoverKvUri, resolvePlatformRepoRoot } from "../../lib/brain-paths.js";
import { resolveSeed, materializeBrainTree, writeSeedMarker } from "../../lib/brain-seed.js";
import { upstreamForSeed } from "../../lib/agent-content-sync.js";
import { createBlankRepo, pushSeed, probeRepoState, appRepoProbe, mintInstallationTokenFromPem, type RepoState } from "../../lib/github-app-repo.js";
import { assertInstallationPermissions } from "../../lib/github-app-permissions.js";
import { resolveRepoCollision, type CollisionChoice } from "../../lib/brain-repo-collision.js";
import { resolveBrainMcpUrl } from "../../lib/bridge-url.js";

/**
 * Interactive collision prompt (only invoked on the gh path when stdout is a TTY).
 */
async function promptCollision(
  ctx: { stdin: NodeJS.ReadStream; stdout: NodeJS.WriteStream },
  info: { repoName: string; state: "absent" | "m8t-brain" | "empty" | "foreign" },
): Promise<CollisionChoice> {
  const rl = readline.createInterface({ input: ctx.stdin, output: ctx.stdout });
  try {
    if (info.state === "foreign") {
      const ans = (await rl.question(
        `A repo "${info.repoName}" already exists but isn't an m8t brain. Create a new "${info.repoName}-2" instead? [Y/n] (n aborts) `,
      )).trim().toLowerCase();
      return ans === "" || ans === "y" || ans === "yes" ? "new" : "abort";
    }
    const ans = (await rl.question(
      `An m8t brain "${info.repoName}" already exists. Reuse it? [Y/n] (n creates "${info.repoName}-2", or "a" aborts) `,
    )).trim().toLowerCase();
    if (ans === "" || ans === "y" || ans === "yes") return "reuse";
    if (ans === "a" || ans === "abort") return "abort";
    return "new";
  } finally {
    rl.close();
  }
}

/**
 * The m8t commit to stamp as the brain's scaffold provenance. Prefers an
 * explicit build SHA (M8T_BUILD_SHA — baked into the cloud installer image), then
 * the repoRoot's git HEAD (a local checkout), and finally a "(scaffold)" placeholder.
 *
 * repoRoot is NOT a git repo in the baked installer image (it's COPY'd plain files),
 * so `git rev-parse` there exits "fatal: not a git repository". stderr is SILENCED so
 * that message can never leak onto a `--output json` stream — a raw non-JSON line
 * there is what aborted the cloud installer's create-brains phase (the rc=4 crash) —
 * and the failure falls back to the placeholder instead of throwing.
 */
export function resolveScaffoldCommit(repoRoot: string, env: NodeJS.ProcessEnv): string {
  const baked = env.M8T_BUILD_SHA?.trim();
  if (baked) return baked;
  try {
    return (
      execFileSync("git", ["rev-parse", "HEAD"], {
        cwd: repoRoot,
        encoding: "utf8",
        stdio: ["ignore", "pipe", "ignore"],
      }).trim() || "(scaffold)"
    );
  } catch {
    return "(scaffold)";
  }
}

function stageAsGitRepo(dir: string): void {
  const steps: { args: string[]; label: string }[] = [
    { args: ["init", "-b", "main"], label: "git init" },
    { args: ["add", "."], label: "git add" },
    { args: ["commit", "-m", "Initial commit from brain-template"], label: "git commit" },
  ];
  for (const { args, label } of steps) {
    const r = spawnSync("git", args, { cwd: dir, encoding: "utf8" });
    if (r.status !== 0) {
      const exitCode = r.status !== null ? r.status.toString() : "<null>";
      const detail = r.stderr.trim() || r.stdout.trim() || `exit ${exitCode}`;
      throw new LocalCliError({
        code: "BRAIN_GIT_INIT_FAILED",
        message: `${label} failed in ${dir}: ${detail}`,
      });
    }
  }
}

export class BrainCreateCommand extends M8tCommand {
  static paths = [["brain", "create"]];
  static usage = Command.Usage({
    description: "Create a new brain repo from brain-template/ (optionally seeded) and link it to <worker>.",
    details:
      "Copies ~/.m8t/repo-root/brain-template/ to a tmp dir, optionally overlays brain-seeds/<name>/ (via --seed), stages it as a git repo, runs gh repo create --source --push, then the link cascade. Default --repo-name: <worker>-brain. Default visibility: private.",
    examples: [
      ["Create + link with defaults", "$0 brain create cmo --owner orkeren21"],
      ["Custom name + public", "$0 brain create cmo --owner orkeren21 --repo-name cmo-second-brain --public"],
      ["Seeded brain (overlay brain-seeds/<name>/)", "$0 brain create cmo --owner orkeren21 --seed <name>"],
    ],
  });

  worker = Option.String();
  owner = Option.String("--owner");
  repoName = Option.String("--repo-name");
  branch = Option.String("--branch");
  public_ = Option.Boolean("--public", false);
  kvUri = Option.String("--kv-uri");
  subscription = Option.String("--subscription");
  endpoint = Option.String("--endpoint");
  output = Option.String("--output");
  seed = Option.String("--seed");
  appId = Option.String("--app-id", { description: "GitHub App id (no-gh path); with --app-pem/--installation-id, create the repo via the App instead of gh." });
  appPem = Option.String("--app-pem", { description: "Path to the App private-key PEM (no-gh path)." });
  installationId = Option.String("--installation-id", { description: "Org installation id for the App (no-gh path)." });
  reuse = Option.Boolean("--reuse", false, { description: "If the brain repo already exists, reuse it (non-interactive). Errors if the repo isn't a valid m8t brain." });
  newName = Option.Boolean("--new", false, { description: "If the brain repo name is taken, create a fresh suffixed repo (e.g. <name>-2) instead of reusing." });
  gatewayUrl = Option.String("--gateway-url", { description: "Base gateway URL (defaults to M8T_GATEWAY_URL or ~/.m8t/config.yaml gatewayUrl)." });
  legacyGithubMcp = Option.Boolean("--legacy-github-mcp", false, { description: "Rollback only: link prompt workers through the legacy generic GitHub MCP." });

  async executeCommand(): Promise<number> {
    // Normalize clipanion descriptor objects that appear when flags are unset in tests.
    const owner = typeof this.owner === "string" ? this.owner : undefined;
    const worker = typeof this.worker === "string" ? this.worker : undefined;
    const repoName = typeof this.repoName === "string"
      ? this.repoName
      : (worker ? `${worker}-brain` : undefined);
    const branch = (typeof this.branch === "string" ? this.branch : undefined) ?? "main";

    if (!owner) {
      throw new LocalCliError({ code: "USAGE", message: "--owner is required (your GitHub username or org)" });
    }
    if (!worker) {
      throw new LocalCliError({ code: "USAGE", message: "<worker> positional argument is required" });
    }
    if (!repoName) {
      throw new LocalCliError({ code: "USAGE", message: "Could not determine repo name" });
    }

    // Resolve + validate the seed early so an unknown name fails fast — before
    // any pre-flight, tmp dir, or repo creation (no partial repo).
    const seedName = typeof this.seed === "string" ? this.seed : undefined;
    const seedDir = seedName ? resolveSeed(seedName) : undefined;

    const env = this.context.env;
    const kvUri = discoverKvUri(env, typeof this.kvUri === "string" ? this.kvUri : undefined);
    const legacyGithubMcp = this.legacyGithubMcp === true;
    const gatewayMcpUrl = legacyGithubMcp
      ? undefined
      : resolveBrainMcpUrl({
          flag: typeof this.gatewayUrl === "string" ? this.gatewayUrl : undefined,
          env,
        });

    // Normalize output: clipanion leaves a descriptor object on the property
    // until argv parsing; when set directly in tests (or not supplied), treat
    // anything that is not a plain string as "pretty" (our diagnostic default).
    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);
    const progress = mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    const credential = new DefaultAzureCredential();

    // Compute useAppPath early — needed to gate the gh-only pre-flights below.
    const appIdOpt = typeof this.appId === "string" ? this.appId : undefined;
    const appPemOpt = typeof this.appPem === "string" ? this.appPem : undefined;
    const installationIdOpt = typeof this.installationId === "string" ? this.installationId : undefined;
    const useAppPath = appIdOpt !== undefined && appPemOpt !== undefined && installationIdOpt !== undefined;

    if (useAppPath) {
      // Pre-flight 0 (App path only): does this installation actually grant what
      // we are about to need? Cheap, and it runs BEFORE the Foundry resolve, the
      // template copy, and the repo create — so a stale installation is a named
      // refusal here rather than a bare 403 from POST /orgs/<org>/repos several
      // minutes and several side effects later. The gh path does not need it:
      // there the repo is created with the operator's own token, and the App is
      // installed interactively afterwards.
      await assertInstallationPermissions({
        appId: appIdOpt,
        privateKeyPem: readFileSync(appPemOpt, "utf8"),
        installationId: installationIdOpt,
        org: owner,
        onUnverified: (m) => this.context.stderr.write(`  ${colors.dim(`warning: ${m}`)}\n`),
      });
    }

    if (!useAppPath) {
      // Pre-flight 1: gh auth
      if (!(await isGhAuthed())) {
        this.context.stdout.write(`${colors.hint("note:")} gh not authed — running gh auth login --device --scopes "repo,read:org"\n`);
        await ghAuthLoginDevice();
      }

      // Pre-flight 2: inline check-app (silent if pass)
      const health = await checkAppHealth({ credential, kvUri });
      if (!health.ok) {
        throw new LocalCliError({
          code: "APP_HEALTH_FAILED",
          ...appHealthFailureCopy(health),
        });
      }
    }

    // Pre-flight 3: resolve Foundry project (for ARM id)
    const account = await getAzAccount();
    const subscriptionId =
      (typeof this.subscription === "string" ? this.subscription : undefined) ??
      account.subscriptionId;
    const agentEndpoint = readAgentYaml(worker)?.projectEndpoint;
    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive: (this.context.stdout as NodeJS.WriteStream).isTTY === true,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
      agentEndpoint,
    });

    // 1. Copy brain-template/ to tmpdir
    const repoRoot = resolvePlatformRepoRoot("'m8t brain create'", ["brain-template"]);
    const templateSrc = path.join(repoRoot, "brain-template");
    if (!fs.existsSync(templateSrc)) {
      throw new LocalCliError({
        code: "TEMPLATE_MISSING",
        message: `brain-template/ missing at ${templateSrc}`,
        hint: "Ensure brain-template/ exists at the repo root (same level as apps/).",
      });
    }
    const tmpDir = fs.mkdtempSync(path.join(os.tmpdir(), `m8t-brain-create-${worker}-`));
    let result: Awaited<ReturnType<typeof linkBrain>>;
    let resolvedInstallationId: string | null = null;

    const force: "reuse" | "new" | undefined =
      this.reuse === true ? "reuse" : this.newName === true ? "new" : undefined;
    // App path (installer) is deterministic; the gh (local) path prompts on a TTY.
    const interactive = !useAppPath && (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    let token: string | undefined;
    let classify: (name: string) => Promise<RepoState>;
    if (useAppPath) {
      token = await mintInstallationTokenFromPem({
        appId: appIdOpt, privateKeyPem: readFileSync(appPemOpt, "utf8"), installationId: installationIdOpt,
      });
      const tok = token;
      classify = (name) => probeRepoState(appRepoProbe({ token: tok, repo: `${owner}/${name}` }));
    } else {
      classify = (name) => probeRepoState(ghRepoProbe({ owner, repo: name }));
    }
    const decision = await resolveRepoCollision({
      repoName, classify, interactive, force,
      prompt: interactive
        ? (info) => promptCollision(
            { stdin: this.context.stdin as NodeJS.ReadStream, stdout: this.context.stdout as NodeJS.WriteStream },
            info,
          )
        : undefined,
    });
    const finalName = decision.repoName;
    const needsPush = decision.action === "create" || decision.action === "adopt";
    if (finalName !== repoName) {
      this.context.stdout.write(`${colors.hint("note:")} using repo name ${colors.field(finalName)} (${decision.action}).\n`);
    }

    try {
      if (needsPush) {
        materializeBrainTree({ templateSrc, destDir: tmpDir, seedDir });
        if (seedName && seedDir) {
          // Write the seed-manifest marker as part of the initial tree, before any
          // pushSeed, so it lands in the first "seed: initial brain" commit and the
          // new repo carries it from day one (no bootstrap needed on first refresh).
          const headSha = resolveScaffoldCommit(repoRoot, env);
          // Where the seed content came from, when it came from a pinned agent repo.
          // `refreshedToCommit` above is a PLATFORM commit and cannot address an
          // agent repo's history, so without this a brain seeded from an agent repo
          // has no reachable merge base on its next refresh. Nothing reads it yet —
          // downstream updates are parked — but a base that was never recorded
          // cannot be reconstructed later, so it is written now.
          const upstream = upstreamForSeed(repoRoot, seedName);
          writeSeedMarker({
            destDir: tmpDir, seedName, seedDir, refreshedToCommit: headSha, version: "(scaffold)",
            ...(upstream ? { upstream } : {}),
          });
        }
        this.context.stdout.write(
          seedName
            ? `${colors.dim("→")} copied brain-template/ + overlaid seed ${colors.field(seedName)} to ${tmpDir}\n`
            : `${colors.dim("→")} copied brain-template/ to ${tmpDir}\n`,
        );
      }

      if (useAppPath) {
        // token is only undefined on the gh path; guarded by useAppPath here.
        const appToken = token ?? "";
        if (decision.action === "create") {
          this.context.stdout.write(`${colors.dim("→")} creating ${owner}/${finalName} via the GitHub App (no gh)…\n`);
          await createBlankRepo({ token: appToken, org: owner, name: finalName, private: !this.public_ });
          await pushSeed({ token: appToken, org: owner, name: finalName, branch, sourceDir: tmpDir });
          this.context.stdout.write(`${colors.success("✓")} repo created + seeded via the App\n`);
        } else if (decision.action === "adopt") {
          this.context.stdout.write(`${colors.dim("→")} seeding the existing empty repo ${owner}/${finalName} via the GitHub App…\n`);
          await pushSeed({ token: appToken, org: owner, name: finalName, branch, sourceDir: tmpDir });
          this.context.stdout.write(`${colors.success("✓")} empty repo seeded via the App\n`);
        } else {
          this.context.stdout.write(`${colors.success("✓")} reusing the existing brain ${owner}/${finalName}\n`);
        }
        resolvedInstallationId = installationIdOpt;
      } else {
        // gh path.
        if (decision.action === "create") {
          stageAsGitRepo(tmpDir);
          this.context.stdout.write(`${colors.dim("→")} creating repo ${owner}/${finalName} (${this.public_ ? "public" : "private"})…\n`);
          await createRepo({ owner, name: finalName, private: !this.public_, source: tmpDir });
          this.context.stdout.write(`${colors.success("✓")} repo created\n`);
        } else if (decision.action === "adopt") {
          // Push the fresh tree into the existing empty repo using the operator's gh
          // token (gh's own https push refuses an existing repo, and a credential-less
          // remote fails when gh uses the SSH protocol) — same token-remote form the App path uses.
          this.context.stdout.write(`${colors.dim("→")} seeding the existing empty repo ${owner}/${finalName}…\n`);
          const ghToken = await getGhToken();
          await pushSeed({ token: ghToken, org: owner, name: finalName, branch, sourceDir: tmpDir });
          this.context.stdout.write(`${colors.success("✓")} empty repo seeded\n`);
        } else {
          this.context.stdout.write(`${colors.success("✓")} reusing the existing brain ${owner}/${finalName}\n`);
        }

        // App install: probe-then-open + poll (runs for create/adopt/reuse — the repo now exists).
        const repoId = await getRepoId(owner, finalName);
        const { slug, appId: secretAppId, privateKeyPem } = await readAppSecrets({ credential, kvUri });
        // Pre-probe — one-shot, safe to use a single JWT.
        const initialJwt = signAppJwt({ appId: secretAppId, privateKeyPem });
        // Probe-then-open: skip browser when the App is already
        // installed on this repo (Ordering 3 idempotent re-link).
        resolvedInstallationId = await probeAppInstallation({ owner, repo: finalName, jwt: initialJwt });
        if (resolvedInstallationId) {
          this.context.stdout.write(`  ${colors.success("✓")} App already installed on ${colors.field(`${owner}/${finalName}`)} (installation ${resolvedInstallationId}); skipping browser open.\n`);
        } else {
          const url = installUrl(slug, repoId);
          this.context.stdout.write(`${colors.field("→")} Install the m8t-brain App on ${owner}/${finalName}:\n  ${url}\n`);
          await tryOpenUrl(url);
          this.context.stdout.write(`  ${colors.dim("waiting for installation (Ctrl-C to cancel)…")}\n`);
          resolvedInstallationId = await pollForInstallation({
            owner, repo: finalName,
            // Mint fresh JWT each tick — App JWTs are 10-min TTL, poll defaults to 5 min
            // but the timeout is a soft default, so don't depend on the inequality.
            probe: () => probeAppInstallation({ owner, repo: finalName, jwt: signAppJwt({ appId: secretAppId, privateKeyPem }) }),
            onTick: (n) => { if (n % 5 === 0) this.context.stderr.write(`  ${colors.dim(`…still waiting (${(n * 2).toString()}s)`)}\n`); },
          });
          this.context.stdout.write(`  ${colors.success("✓")} installation detected (id: ${resolvedInstallationId})\n`);
        }
      }

      // 5. Link cascade
      result = await linkBrain({
        credential, kvUri,
        projectEndpoint: project.endpoint,
        projectArmId: `${project.accountScope}/projects/${project.projectName}`,
        agentName: worker,
        repo: `${owner}/${finalName}`, branch,
        gatewayMcpUrl,
        legacyGithubMcp,
        repoRoot, installationId: resolvedInstallationId,
        skipIfLinked: true,
        onProgress: progress,
        subscriptionId,
        project,
      });
    } finally {
      fs.rmSync(tmpDir, { recursive: true, force: true });
    }

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          worker, repo: `${owner}/${finalName}`, branch,
          installationId: resolvedInstallationId, connectionName: result.connectionName,
          foundryVersion: result.foundryVersion,
        }) + "\n",
      );
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} created + linked ${colors.field(worker)} ↔ ${colors.field(`${owner}/${finalName}`)} (agent version ${result.foundryVersion ?? ""}).\n`,
    );
    this.context.stdout.write(`  ${colors.hint("brain repo:")} https://github.com/${owner}/${finalName}\n`);
    this.context.stdout.write(
      legacyGithubMcp
        ? `  ${colors.hint("token:")} legacy GitHub installation token rotates lazily on invoke.\n`
        : `  ${colors.hint("access:")} scoped to the five brain tools on the linked repo + branch.\n`,
    );
    return 0;
  }
}
